import { useMemo } from 'react';
import { useCreateBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { AdvancedSearchResponse } from 'src/serverProxy/useTableData';
import { useAppSelector } from 'src/utils/Hooks';
import useGenericAdvancedSearch, { UseGenericAdvancedSearchParameters } from 'src/utils/Hooks/useGenericAdvancedSearch';
import _get from 'lodash/get';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { getQueryParamValue } from 'src/utils/app';
import { BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

export interface UseAdvancedSearchDataField {
  name: string;
}

export interface UseAdvancedSearchDataArgs {
  fields: UseAdvancedSearchDataField[];
  groupByField: string;
  indexName: string;
  requestBuilder?: (
    builder: AdvancedSearchRequestBuilder,
    comparison: boolean,
    fields: UseAdvancedSearchDataField[]
  ) => AdvancedSearchRequestBuilder;

  /**
   * Optionally override the default request id for easier request identification
   */
  requestId?: string;
  /**
   * Optionally return the raw response from the server instead of using the default parsing behavior.
   */
  returnRawResponse?: boolean;
  /**
   * Optionally include the raw response in the return object
   */
  includeRawResponse?: boolean;

  /**
   * Optionally fetch the comparison data. Defaults to true.
   */
  shouldFetchComparison?: boolean;

  primaryGenericAdvancedSearchArgs?: Omit<UseGenericAdvancedSearchParameters, 'queryKeys' | 'requestBody'>;
  comparisonGenericAdvancedSearchArgs?: Omit<UseGenericAdvancedSearchParameters, 'queryKeys' | 'requestBody'>;
  addFieldsFromPrimaryResponse?: boolean;
}

export interface AdvancedSearchParsedDatum {
  fieldId: string;
  values: Record<string, number>;
  comparisonValues: Record<string, number>;
  metadata: Record<string, any>;
  comparisonMetadata: Record<string, any>;
  rawResponse: Record<string, any>;
}
/**
 * Accepts an arbitrary number of index/field name combinations to return the
 * combined data in a single array where each item is an object containing
 * the value and comparison value for each field.
 */
export default function useAdvancedSearchData({
  fields,
  groupByField,
  indexName,
  requestBuilder,
  requestId = 'advancedSearchData',
  returnRawResponse = false,
  includeRawResponse,
  shouldFetchComparison = true,
  primaryGenericAdvancedSearchArgs = {},
  comparisonGenericAdvancedSearchArgs = {},
  addFieldsFromPrimaryResponse
}: UseAdvancedSearchDataArgs) {
  const currentTab = getQueryParamValue('tab');
  const { startWeek, endWeek, startDayId, endDayId } = useAppSelector((state) => state.mainTimePeriod);

  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek, startDayId: comparisonStartDayId, endDayId: comparisonEndDayId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const createBaseMetricRequestBuilder = useCreateBaseMetricRequestBuilder();

  /**
   * There will be two objects in the request - one for
   * the current time period and one for the comparison time period. This is the
   * array of request bodies that will be sent to the server.
   */
  const primaryRequestBuilder = useMemo(() => {
    const builder = createBaseMetricRequestBuilder({
      fields,
      indexName,
      requestId,
      groupByFieldName: groupByField
    })
      .setPageSize(100);

    if (currentTab === BEACON_TABS.ADVERTISING) {
      builder.addConditionRangeFilter('dayId', startDayId, endDayId)
        .modifyAggregation((aggBuilder) => aggBuilder.addConditionRangeFilter('dayId', startDayId, endDayId));
    } else {
      builder.addConditionRangeFilter('weekId', startWeek, endWeek)
        .modifyAggregation((aggBuilder) => aggBuilder.addConditionRangeFilter('weekId', startWeek, endWeek));
    }

    if (requestBuilder) {
      requestBuilder(builder, false, fields);
    }

    return builder;
  }, [createBaseMetricRequestBuilder, fields, indexName, requestId, groupByField, currentTab, requestBuilder, startWeek, endWeek, startDayId, endDayId]);

  // Primary data
  const { data: response, isLoading } = useGenericAdvancedSearch<AdvancedSearchResponse<any>[]>({
    requestBody: [primaryRequestBuilder.build()],
    queryKeys: [primaryRequestBuilder.build()],
    requestId,
    ...primaryGenericAdvancedSearchArgs
  });

  // Comparison request bodies - we need to add the fields returned from the primary data as
  // term filters in order to get the data for the same set
  const comparisonRequestBuilder = useMemo(() => {
    const builder = primaryRequestBuilder
      .clone()
      .clearConditionRangeFilters();

    if (currentTab === BEACON_TABS.ADVERTISING) {
      builder.addConditionRangeFilter('dayId', comparisonStartDayId, comparisonEndDayId); // Comparison date range
    } else {
      builder.addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek); // Comparison date range
    }


    if (addFieldsFromPrimaryResponse) {
      builder.addConditionTermFilter(
        groupByField,
        'should',
        // Add in the field IDs from the primary data as term filters
        _get(response, ['data', 0, 'aggregations', `by_${groupByField}`], []).map((row) => row.fieldId)
      );
    }

    if (currentTab === BEACON_TABS.ADVERTISING) {
      builder.modifyAggregation((aggBuilder) =>
        aggBuilder.clearConditionRangeFilters().addConditionRangeFilter('dayId', comparisonStartDayId, comparisonEndDayId)
      );
    } else {
      builder.modifyAggregation((aggBuilder) =>
        aggBuilder.clearConditionRangeFilters().addConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek)
      );
    }

    if (requestBuilder) {
      requestBuilder(builder, true, fields);
    }

    return builder;
  }, [primaryRequestBuilder, currentTab, addFieldsFromPrimaryResponse, requestBuilder, comparisonStartDayId, comparisonEndDayId, comparisonStartWeek, comparisonEndWeek, groupByField, response, fields]);

  // Secondary data request
  const { data: comparisonResponse, isLoading: compareLoading } = useGenericAdvancedSearch<
    AdvancedSearchResponse<any>[]
  >({
    shouldPerformFetch: !isLoading && shouldFetchComparison,
    queryKeys: [comparisonRequestBuilder.build()],
    requestBody: [comparisonRequestBuilder.build()],
    requestId: `${requestId}_comparison`,
    ...comparisonGenericAdvancedSearchArgs
  });

  /**
   * Merge the primary and comparison data for each index into a single array
   */
  const parsedData: AdvancedSearchParsedDatum[] = useMemo(() => {
    if (returnRawResponse) {
      return null;
    }

    if (isLoading || compareLoading) {
      return [];
    }

    const responseBodies = _get(response, 'data', []);
    const comparisonResponseBodies = _get(comparisonResponse, 'data', []);

    const primaryData = _get(responseBodies, [0, 'aggregations', `by_${groupByField}`], []);
    const comparisonData = _get(comparisonResponseBodies, [0, 'aggregations', `by_${groupByField}`], []);

    // Since comparison data won't be in the same order as primary, we map the field to its data
    // so we can combine primary and secondary data in constant time
    const comparisonDataMap = comparisonData.reduce((acc, row) => {
      return {
        ...acc,
        [row.fieldId]: row
      };
    }, {});

    // Fields might be missing from either the primary or comparison data, so we need to combine them
    const allFieldIds = new Set([
      ...primaryData.map((row) => row.fieldId),
      ...comparisonData.map((row) => row.fieldId)
    ]);

    // Combine primary and comparison data into a single array
    return Array.from(allFieldIds).map((fieldId) => {
      const primaryRow = primaryData.find((row) => row.fieldId === fieldId) || {};
      const comparisonRow = comparisonDataMap[fieldId] || {};

      return {
        fieldId,
        values: primaryRow.additionalValues || {},
        comparisonValues: comparisonRow.additionalValues || {},
        metadata: primaryRow.additionalMetaData || comparisonRow.additionalMetaData || {},
        comparisonMetadata: comparisonRow.additionalMetaData || {},
        rawResponse: includeRawResponse ? { primary: responseBodies, comparison: comparisonResponseBodies } : null
      };
    });
  }, [compareLoading, comparisonResponse, groupByField, isLoading, response, includeRawResponse, returnRawResponse]);
  const defaultRawResponses = [...(response ? [response] : []), ...(comparisonResponse ? [comparisonResponse] : [])];
  const data = returnRawResponse ? defaultRawResponses : parsedData;

  return {
    data,
    isLoading: isLoading || compareLoading
  };
}
